/* LU ZEV | Navigation for Horizon's existing product image viewer.
   Delegated events also work after variant changes replace the gallery HTML. */
(() => {
  if (window.__luzevGalleryNavigation) return;
  window.__luzevGalleryNavigation = true;

  const getViewer = (el) => {
    const viewer = el && el.closest('zoom-dialog');
    return viewer && viewer.closest('media-gallery.luzev-media-gallery') ? viewer : null;
  };

  const getActiveIndex = (viewer) => {
    const rail = viewer.querySelector('.dialog-zoomed-gallery');
    if (!rail) return 0;
    const slides = Array.from(rail.children);
    if (!slides.length) return 0;
    const mid = rail.getBoundingClientRect().left + rail.clientWidth / 2;
    let best = 0;
    let distance = Infinity;
    slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const d = Math.abs((rect.left + rect.right) / 2 - mid);
      if (d < distance) {
        best = i;
        distance = d;
      }
    });
    return best;
  };

  const navigate = (viewer, step) => {
    const count = viewer.querySelectorAll('.dialog-zoomed-gallery > li').length;
    if (count < 2 || typeof viewer.selectThumbnail !== 'function') return;
    const nextIndex = (getActiveIndex(viewer) + step + count) % count;
    viewer.dataset.luzevActive = String(nextIndex);
    viewer.selectThumbnail(nextIndex, {
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('[data-luzev-zoom-step]')
      : null;
    if (!button) return;
    const viewer = getViewer(button);
    if (!viewer) return;
    event.preventDefault();
    navigate(viewer, Number(button.dataset.luzevZoomStep));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const dialog = document.querySelector('media-gallery.luzev-media-gallery zoom-dialog dialog[open]');
    if (!dialog) return;
    const viewer = getViewer(dialog);
    if (!viewer) return;
    event.preventDefault();
    const rtl = document.documentElement.dir === 'rtl';
    const dir = event.key === 'ArrowLeft' ? -1 : 1;
    navigate(viewer, rtl ? -dir : dir);
  });

  // The built-in viewer detects scroll on its dialog. Our horizontal rail scrolls
  // inside it, so synchronize its native selection after a swipe as well.
  const timers = new WeakMap();
  document.addEventListener('scroll', (event) => {
    const rail = event.target;
    if (!(rail instanceof Element) || !rail.matches('.luzev-media-gallery .dialog-zoomed-gallery')) return;
    clearTimeout(timers.get(rail));
    timers.set(rail, setTimeout(() => {
      const viewer = getViewer(rail);
      if (!viewer || !viewer.querySelector('dialog[open]')) return;
      const activeIndex = getActiveIndex(viewer);
      if (viewer.dataset.luzevActive === String(activeIndex)) return;
      viewer.dataset.luzevActive = String(activeIndex);
      viewer.selectThumbnail(activeIndex, { behavior: 'instant' });
    }, 100));
  }, true);

  // On desktop, open zoom consistently when a user clicks the actual main image.
  // Intercept in capture phase to avoid double-opening through slideshow's handler.
  document.addEventListener('click', (event) => {
    if (window.innerWidth < 750 || !(event.target instanceof Element)) return;
    const image = event.target.closest('slideshow-slide.product-media-container--zoomable .product-media__image');
    if (!image) return;
    const gallery = image.closest('media-gallery.luzev-media-gallery');
    if (!gallery || typeof gallery.zoom !== 'function') return;
    const slide = image.closest('slideshow-slide');
    const slides = Array.from(gallery.querySelectorAll('slideshow-component slideshow-slide'));
    const index = slides.indexOf(slide);
    if (index < 0 || !gallery.querySelector('zoom-dialog')) return;
    event.preventDefault();
    event.stopPropagation();
    gallery.zoom(index, event);
  }, true);

  // The native desktop dialog is a fit-to-screen viewer. Clicking a fullscreen
  // photo deliberately toggles 2.2x magnification; mobile retains pinch/double-tap.
  const resetDesktopZoom = (viewer) => {
    viewer?.querySelectorAll('.dialog-zoomed-gallery .luzev-zoomed').forEach((item) => {
      item.classList.remove('luzev-zoomed');
      item.style.removeProperty('--luzev-zoom-origin');
    });
  };

  document.addEventListener('click', (event) => {
    if (window.innerWidth < 750 || !(event.target instanceof Element)) return;
    const image = event.target.closest('.dialog-zoomed-gallery .product-media-container--image .product-media__image');
    if (!image) return;
    const viewer = getViewer(image);
    if (!viewer?.querySelector('dialog[open]')) return;
    const item = image.closest('.product-media-container--image');
    if (!item) return;
    event.preventDefault();
    event.stopPropagation();
    const willZoom = !item.classList.contains('luzev-zoomed');
    resetDesktopZoom(viewer);
    if (willZoom) {
      const rect = image.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
      item.style.setProperty('--luzev-zoom-origin', `${x}% ${y}%`);
      item.classList.add('luzev-zoomed');
    }
  }, true);

  // Navigation and dialog closing should not retain zoom on another image.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('[data-luzev-zoom-step], .luzev-zoom-actions .dialog-zoomed-gallery__close-button');
    if (!control) return;
    resetDesktopZoom(getViewer(control));
  }, true);
  document.addEventListener('close', (event) => {
    if (event.target instanceof HTMLDialogElement) resetDesktopZoom(getViewer(event.target));
  }, true);

})();
